import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Footer, Pagination, Table } from "rsuite";
import { OrderServices } from "../../../core/services/OrderServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { setActionData } from "../../../redux/userActionData";
import { OrderFields, OrderListParams, Permission } from "./OrderDataTypes";
import "./Orders.css";
import {
  formatDateOnly,
  isEmptyVal,
  processMinute,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import VisibleIcon from "@rsuite/icons/Visible";
import { ToastContainer } from "react-toastify";
import { displayField } from "../../../utils/helpers";
import { appConstObj } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import GroupedOrderSingleAction from "./GroupedOrderSingleAction";
import ViewOrdersModal from "./ViewOrdersModal";

function OrderScheduleForDay({
  forCompany,
  forStore,
  forDay,
  ...props
}: {
  forCompany?: any;
  forStore?: any;
  forDay?: any;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OrderFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const [openModalByName, setOpenModalByName] = useState("");

  const initSearchFormData = {
    company: forCompany,
    store: forStore,
    order_serve_at: "",
    order_assigned_to: "",
    service_date: forDay,
    served_by: "",
    order_status: "",
    service_hour: "",
  };

  const [formData, setFormData] = useState<OrderListParams>(initSearchFormData);

  async function getGroupedOrders(params: OrderListParams) {
    try {
      setLoading(true);
      const payload = {
        ...params,
        service_date: params.service_date
          ? formatDateOnly(params.service_date)
          : null,
      };
      const res = await OrderServices.getGroupedOrders(payload);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    getGroupedOrders(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: OrderListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getGroupedOrders(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getGroupedOrders(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getGroupedOrders(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const handleEvent = (eventKey: any) => {
    setOpenModalByName(eventKey);
    //dispatch(setActionData(initSearchFormData));
  };

  return (
    <div>
      <div
        className={
          total > 0 ? "cust-finder-table mt-3" : "no-record-table mt-3"
        }
      >
        <Table
          height={50}
          fillHeight={true}
          data={data}
          loading={loading}
          onSortColumn={onSortChange}
          sortColumn={sort_by}
          bordered={true}
          cellBordered
          sortType={sort_order}
          hover={true}
        >
          <Column width={120} sortable resizable>
            <CustomHeaderCell>
              {t("SERVICE")} {t("HOUR")}
            </CustomHeaderCell>
            <CustomCell dataKey="service_hour">
              {(rowData: any) => (
                <span>{displayField(rowData.service_hour)}</span>
              )}
            </CustomCell>
          </Column>

          <Column width={120} sortable>
            <CustomHeaderCell>
              {t("ORDER")} {t("COUNT")}
            </CustomHeaderCell>
            <CustomCell dataKey="order_count">
              {(rowData: any) => (
                <span>{displayField(rowData.order_count)}</span>
              )}
            </CustomCell>
          </Column>

          <Column width={120} sortable>
            <CustomHeaderCell>
              {t("TOTAL")} {t("TIME")}
            </CustomHeaderCell>
            <CustomCell dataKey="total_service_time">
              {(rowData: any) => (
                <span>{processMinute(rowData.total_service_time)}</span>
              )}
            </CustomCell>
          </Column>
          <Column width={140} sortable>
            <CustomHeaderCell align="left">
              {t("TOTAL")} {t("ORDER")} {t("VALUE")}
            </CustomHeaderCell>
            <CustomCell dataKey="total_order_payable">
              {(rowData: any) => (
                <span>{displayField(rowData.total_order_payable)}</span>
              )}
            </CustomCell>
          </Column>
          <Column flexGrow={1} align="left">
            <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
            <GroupedOrderSingleAction orderFilterData={initSearchFormData} />
          </Column>
        </Table>
      </div>
      <Footer className="footer">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={total}
          limitOptions={appConstObj.limit_options}
          limit={rows}
          activePage={page}
          onChangePage={onPageChange}
          onChangeLimit={handleChangeRows}
        />
      </Footer>
      <ToastContainer />
      {openModalByName === "view-orders" && (
        <ViewOrdersModal
          orderFilterData={formData}
          openModal={openModalByName === "view-orders" ? true : false}
          setOpenModal={setOpenModalByName}
        />
      )}
    </div>
  );
}

export default OrderScheduleForDay;
